import toast from 'react-hot-toast';

import { useMutation } from '@tanstack/react-query';

import getCSRFToken from '../../../utils/CSRFToken';
import { useBrandStore } from '../context';
import { COLOUR_KEYS, TEXT_KEYS, CONTAINER_KEYS, SPACING_KEYS, RADIUS_KEYS, SHADOW_KEYS, BLUR_KEYS, MISC_KEYS } from '../lib';

import TextInput from '../../../inputs/form-inputs/TextInput';
import ColourPickerInputs from './_inputs/ColourPickerInputs';
import CustomStylesInput from './_inputs/CustomStylesInput';
import MediaUploader from './_inputs/MediaUploader';
import StyleInput from './_inputs/StyleInput';
import FontUploaders from './_inputs/FontUploaders';
import Card from '../../../card/Card';
import UnitValueSlider from './_inputs/UnitValueSlider';

export default function DynamicBrandDashboard(props) {
  const { endpoints } = props;
  const { createOrUpdateUrl, deleteUrl } = endpoints;

  // Selectors for state values
  const isLoading = useBrandStore((state) => state.isLoading);
  const setIsLoading = useBrandStore((state) => state.setIsLoading);
  const themeView = useBrandStore((state) => state.themeView);
  const setThemeView = useBrandStore((state) => state.setThemeView);
  const siteName = useBrandStore((state) => state.siteName);
  const setSiteName = useBrandStore((state) => state.setSiteName);
  const siteSlogan = useBrandStore((state) => state.siteSlogan);
  const setSiteSlogan = useBrandStore((state) => state.setSiteSlogan);
  const styles = useBrandStore((state) => state.styles);
  const setStyle = useBrandStore((state) => state.setStyle);

  // Site Logo
  const siteLogo = useBrandStore((state) => state.siteLogo);
  const savedSiteLogo = useBrandStore((state) => state.savedSiteLogo);
  const isDeleteSiteLogo = useBrandStore((state) => state.isDeleteSiteLogo);
  const setSiteLogo = useBrandStore((state) => state.setSiteLogo);
  const setSavedSiteLogo = useBrandStore((state) => state.setSavedSiteLogo);
  const setIsDeleteSiteLogo = useBrandStore((state) => state.setIsDeleteSiteLogo);

  // Site Logo Secondary
  const siteLogoSecondary = useBrandStore((state) => state.siteLogoSecondary);
  const savedSiteLogoSecondary = useBrandStore((state) => state.savedSiteLogoSecondary);
  const isDeleteSiteLogoSecondary = useBrandStore((state) => state.isDeleteSiteLogoSecondary);
  const setSiteLogoSecondary = useBrandStore((state) => state.setSiteLogoSecondary);
  const setSavedSiteLogoSecondary = useBrandStore((state) => state.setSavedSiteLogoSecondary);
  const setIsDeleteSiteLogoSecondary = useBrandStore((state) => state.setIsDeleteSiteLogoSecondary);

  // Site Favicon
  const siteFavicon = useBrandStore((state) => state.siteFavicon);
  const savedSiteFavicon = useBrandStore((state) => state.savedSiteFavicon);
  const isDeleteSiteFavicon = useBrandStore((state) => state.isDeleteSiteFavicon);
  const setSiteFavicon = useBrandStore((state) => state.setSiteFavicon);
  const setSavedSiteFavicon = useBrandStore((state) => state.setSavedSiteFavicon);
  const setIsDeleteSiteFavicon = useBrandStore((state) => state.setIsDeleteSiteFavicon);

  // Site OG Image
  const siteOgImage = useBrandStore((state) => state.siteOgImage);
  const savedSiteOgImage = useBrandStore((state) => state.savedSiteOgImage);
  const isDeleteSiteOgImage = useBrandStore((state) => state.isDeleteSiteOgImage);
  const setSiteOgImage = useBrandStore((state) => state.setSiteOgImage);
  const setSavedSiteOgImage = useBrandStore((state) => state.setSavedSiteOgImage);
  const setIsDeleteSiteOgImage = useBrandStore((state) => state.setIsDeleteSiteOgImage);

  // Site Font
  const siteFont = useBrandStore((state) => state.siteFont);
  const isDeleteSiteFont = useBrandStore((state) => state.isDeleteSiteFont);

  // Site Font Secondary
  const siteFontSecondary = useBrandStore((state) => state.siteFontSecondary);
  const isDeleteSiteFontSecondary = useBrandStore((state) => state.isDeleteSiteFontSecondary);

  // Site Font Tertiary
  const siteFontTertiary = useBrandStore((state) => state.siteFontTertiary);
  const isDeleteSiteFontTertiary = useBrandStore((state) => state.isDeleteSiteFontTertiary);

  const createOrUpdateMutation = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${createOrUpdateUrl}.json`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', ...getCSRFToken() },
        body: JSON.stringify({
          dynamic_brand: {
            site_name: siteName,
            site_slogan: siteSlogan,
            styles: styles,
            is_delete_site_logo: isDeleteSiteLogo,
            is_delete_site_logo_secondary: isDeleteSiteLogoSecondary,
            is_delete_site_favicon: isDeleteSiteFavicon,
            is_delete_site_font: isDeleteSiteFont,
            is_delete_site_font_secondary: isDeleteSiteFontSecondary,
            is_delete_site_font_tertiary: isDeleteSiteFontTertiary,
            is_delete_site_og_image: isDeleteSiteOgImage,
          },
          site_logo: {
            filename: siteLogo.filename,
            base64_file_data: siteLogo.base64_file_data,
          },
          site_logo_secondary: {
            filename: siteLogoSecondary.filename,
            base64_file_data: siteLogoSecondary.base64_file_data,
          },
          site_favicon: {
            filename: siteFavicon.filename,
            base64_file_data: siteFavicon.base64_file_data,
          },
          site_font: {
            filename: siteFont.filename,
            base64_file_data: siteFont.base64_file_data,
          },
          site_font_secondary: {
            filename: siteFontSecondary.filename,
            base64_file_data: siteFontSecondary.base64_file_data,
          },
          site_font_tertiary: {
            filename: siteFontTertiary.filename,
            base64_file_data: siteFontTertiary.base64_file_data,
          },
          site_og_image: {
            filename: siteOgImage.filename,
            base64_file_data: siteOgImage.base64_file_data,
          },
        }),
      });

      if (!response.ok) {
        const errors = await response.json();
        const errorMessage = JSON.stringify(errors?.error) || 'An error occurred while creating or updating the dynamic brand';
        throw new Error(errorMessage);
      }
    },
    onMutate: () => setIsLoading(true),
    onError: (error) => {
      toast.error(`Error: ${error.message || 'An error occurred while saving changes'}`);
    },
    onSuccess: () => {
      window.location.reload();
    },
    onSettled: () => setIsLoading(false),
  });

  return (
    <div className="db-dashboard">
      <div className="d-flex justify-content-end gap-3">
        <div className="btn-group">
          <button type="button" className="btn btn-primary" onClick={() => createOrUpdateMutation.mutate()} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
          <button type="button" className="btn btn-primary dropdown-toggle dropdown-toggle-split" data-bs-toggle="dropdown" aria-expanded="false">
            <span className="visually-hidden">Toggle Dropdown</span>
          </button>
          <ul className="dropdown-menu">
            <li>
              <a href={deleteUrl} className="dropdown-item" data-confirm="Are you sure?" data-link-type="delete" rel="nofollow" data-method="delete">
                Delete all values and start over
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="d-flex flex-column gap-3">
        <h2 className="mb-0">Branding</h2>
        <div className="d-flex flex-column gap-3 p-3 border rounded">
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-3 align-items-center">
              <label className="text-muted">Site Logo (Primary)</label>
              {savedSiteLogo && (
                <a href={savedSiteLogo} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2">
                  <i className="fe fe-external-link" />
                </a>
              )}
            </div>
            <MediaUploader mediaUrl={savedSiteLogo} onDeleteMedia={setIsDeleteSiteLogo} onSetMedia={setSiteLogo} onSetSavedMedia={setSavedSiteLogo} />
          </div>
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-3 align-items-center">
              <label className="text-muted">Site Logo (Secondary)</label>
              {savedSiteLogoSecondary && (
                <a href={savedSiteLogoSecondary} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2">
                  <i className="fe fe-external-link" />
                </a>
              )}
            </div>
            <MediaUploader
              mediaUrl={savedSiteLogoSecondary}
              onDeleteMedia={setIsDeleteSiteLogoSecondary}
              onSetMedia={setSiteLogoSecondary}
              onSetSavedMedia={setSavedSiteLogoSecondary}
            />
          </div>
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-3 align-items-center">
              <label className="text-muted">Site Favicon</label>
              {savedSiteFavicon && (
                <a href={savedSiteFavicon} target="_blank" rel="noopener noreferrer" className="d-flex align-items-center gap-2">
                  <i className="fe fe-external-link" />
                </a>
              )}
            </div>
            <MediaUploader
              mediaUrl={savedSiteFavicon}
              onDeleteMedia={setIsDeleteSiteFavicon}
              onSetMedia={setSiteFavicon}
              onSetSavedMedia={setSavedSiteFavicon}
              acceptedFileTypes={['image/vnd.microsoft.icon', 'image/x-icon', 'image/svg+xml']}
              labelFileTypeNotAllowed="Only .ico files are allowed"
              fileValidateTypeLabelExpectedTypes="Allowed file types: {allTypes}"
            />
          </div>

          <div className="d-flex gap-3 w-100 mb-3">
            <div className="d-flex flex-column gap-2 w-100">
              <label className="text-muted">Site Name</label>
              <TextInput placeholder="Enter your site name..." value={siteName} onChange={(e) => setSiteName(e.target.value)} />
            </div>
            <div className="d-flex flex-column gap-2 w-100">
              <label className="text-muted">Site Slogan</label>
              <TextInput placeholder="Enter your site slogan..." value={siteSlogan} onChange={(e) => setSiteSlogan(e.target.value)} />
            </div>
          </div>

          <div className="d-flex flex-column gap-2">
            <label className="text-muted">Default site OG Image</label>
            <MediaUploader
              mediaUrl={savedSiteOgImage}
              onDeleteMedia={setIsDeleteSiteOgImage}
              onSetMedia={setSiteOgImage}
              onSetSavedMedia={setSavedSiteOgImage}
              acceptedFileTypes={['image/png', 'image/jpeg']}
              labelFileTypeNotAllowed="Only .png and .jpg files are allowed"
              fileValidateTypeLabelExpectedTypes="Allowed file types: {allTypes}"
              maxFileSize="2MB"
              labelMaxFileSize="File should not be larger than 2MB"
            />
          </div>
        </div>
      </div>
      <div className="d-flex flex-column gap-3">
        <h2 className="mb-0">Theme</h2>
        <div className="db-dashboard-theme">
          <div className="tabs">
            <button className={`tab ${themeView === 0 ? 'active' : ''}`} onClick={() => setThemeView(0)}>
              Colours
            </button>
            <button className={`tab ${themeView === 1 ? 'active' : ''}`} onClick={() => setThemeView(1)}>
              Text
            </button>
            <button className={`tab ${themeView === 2 ? 'active' : ''}`} onClick={() => setThemeView(2)}>
              Containers
            </button>
            <button className={`tab ${themeView === 3 ? 'active' : ''}`} onClick={() => setThemeView(3)}>
              Spacing
            </button>
            <button className={`tab ${themeView === 4 ? 'active' : ''}`} onClick={() => setThemeView(4)}>
              Radius
            </button>
            <button className={`tab ${themeView === 5 ? 'active' : ''}`} onClick={() => setThemeView(5)}>
              Shadows
            </button>
            <button className={`tab ${themeView === 6 ? 'active' : ''}`} onClick={() => setThemeView(6)}>
              Blurs
            </button>
            <button className={`tab ${themeView === 7 ? 'active' : ''}`} onClick={() => setThemeView(7)}>
              Misc.
            </button>
            <button className={`tab ${themeView === 8 ? 'active' : ''}`} onClick={() => setThemeView(8)}>
              Custom
            </button>
          </div>
          <div className="content">
            {themeView === 0 && (
              <Card cardClass="rounded-0">
                <ColourPickerInputs styleName="colours" keys={COLOUR_KEYS} styleObj={styles.colours} setStyle={setStyle} styles={styles} />
              </Card>
            )}
            {themeView === 1 && (
              <Card cardClass="rounded-0">
                <StyleInput
                  styleName="texts"
                  keys={TEXT_KEYS}
                  styleObj={styles.texts}
                  setStyle={setStyle}
                  styles={styles}
                  InputComponent={UnitValueSlider}
                  isUseDefaultValue
                />
              </Card>
            )}
            {themeView === 2 && (
              <Card cardClass="rounded-0">
                <StyleInput
                  styleName="container"
                  keys={CONTAINER_KEYS}
                  styleObj={styles.containers}
                  setStyle={setStyle}
                  styles={styles}
                  InputComponent={UnitValueSlider}
                  isUseDefaultValue
                  max={128}
                  step={1}
                />
              </Card>
            )}
            {themeView === 3 && (
              <Card cardClass="rounded-0">
                <StyleInput
                  styleName="spacing"
                  keys={SPACING_KEYS}
                  styleObj={styles.spacings}
                  setStyle={setStyle}
                  styles={styles}
                  InputComponent={UnitValueSlider}
                  isUseDefaultValue
                  min={-1}
                />
              </Card>
            )}
            {themeView === 4 && (
              <Card cardClass="rounded-0">
                <StyleInput
                  styleName="radius"
                  keys={RADIUS_KEYS}
                  styleObj={styles.radius}
                  setStyle={setStyle}
                  styles={styles}
                  InputComponent={UnitValueSlider}
                  isUseDefaultValue
                  max={4}
                />
              </Card>
            )}
            {themeView === 5 && (
              <Card cardClass="rounded-0">
                <StyleInput styleName="shadows" keys={SHADOW_KEYS} styleObj={styles.shadows} setStyle={setStyle} styles={styles} />
              </Card>
            )}
            {themeView === 6 && (
              <Card cardClass="rounded-0">
                <StyleInput
                  styleName="blurs"
                  keys={BLUR_KEYS}
                  styleObj={styles.blurs}
                  setStyle={setStyle}
                  styles={styles}
                  InputComponent={UnitValueSlider}
                  isUseDefaultValue
                  max={128}
                  step={1}
                />
              </Card>
            )}
            {themeView === 7 && (
              <Card cardClass="rounded-0">
                <ColourPickerInputs styleName="misc" keys={MISC_KEYS} styleObj={styles.misc} setStyle={setStyle} styles={styles} />
              </Card>
            )}
            {themeView === 8 && (
              <Card cardClass="rounded-0">
                <CustomStylesInput styleObj={styles.custom} setStyle={setStyle} styles={styles} />
              </Card>
            )}
          </div>
        </div>
      </div>
      <div className="d-flex flex-column gap-3">
        <h2 className="mb-0">Fonts</h2>
        <FontUploaders />
      </div>
    </div>
  );
}
